import SyosEvents from './utils/SyosEvents';
import templateCart from './templates/cart-template';
import { loadModal } from '../AsyncLoader';
import dom from '../utils/dom';
import SyosRemoveSeatConfirmationModal from './modals/SyosRemoveSeatConfirmationModal';
import { ID_CART_SUMMARY } from './SyosMobileCartControls';
import type CurrencyFormatter from '../tnew/currency/CurrencyFormatter';
import type SyosController from './SyosController';
import type SyosSeat from './SyosSeat';
import type ConfirmationModalHandle from '../tnew/ui/Modal/ConfirmationModalHandle';
import { IPriceTypeSelectionHelper } from './priceTypeSelectionHelpers/types';
import { ISyosLabels } from './labels/types';

class SyosCartContainer {
  private seats = new Map<number, SyosSeat>();

  $modalBody: HTMLElement;

  modal?: ConfirmationModalHandle;

  constructor(private syosController: SyosController, private currencyFormatter: CurrencyFormatter, private priceTypeSelectionHelper: IPriceTypeSelectionHelper, private labels: ISyosLabels) {
    this.$modalBody = dom.makeElement('div');
    this.$modalBody.addEventListener('click', this.onModalBodyClick);

    this.syosController.on(SyosEvents.SEAT_SELECTED, this.onSeatSelected);
    this.syosController.on(SyosEvents.SEAT_REMOVED, this.onSeatRemoved);
    this.syosController.on(SyosEvents.CART_DETAILS_OPEN, this.openModal);
  }

  openModal = async () => {
    const Modal = await loadModal();

    this.modal = Modal.confirm({
      title: this.labels.selectedSeatsHeader,
      bodyContents: this.$modalBody,
      labelBtnConfirm: this.labels.addToCartButton,
      onConfirm: () => {
        this.modal?.close();

        this.syosController.emit(SyosEvents.RESERVE_SEATS);
      },
      beforeClose: this.onModalClose,
      className: 'tn-syos-cart-modal',
      headingId: templateCart.HEADING_ID,
    });

    const $btnConfirm = this.modal.getButtonConfirm();

    $btnConfirm.setAttribute('aria-describedby', ID_CART_SUMMARY);

    this.updateModalConfirmButtonState();
  };

  onModalClose = () => {
    this.modal = undefined;
  };

  getSubtotal() {
    const { seats } = this;

    if (seats.size === 0) {
      return 0;
    }

    return Array.from(seats.values())
      .reduce((sum, seat) => sum + seat.priceWithFee, 0);
  }

  getSubtotalDisplay() {
    const subtotal = this.getSubtotal();

    return this.currencyFormatter.format(subtotal);
  }

  getSeatForId(id: number) {
    if (this.seats.has(id)) {
      return this.seats.get(id);
    }
    return null;
  }

  onSeatSelected = ({ seat }: { seat: SyosSeat }) => {
    this.seats.set(seat.id, seat);

    this.updatePriceTypeSelectionHelper();

    this.update();
  };

  onSeatRemoved = ({ seat }: { seat: SyosSeat }) => {
    this.seats.delete(seat.id);

    this.updatePriceTypeSelectionHelper();

    if (this.modal && this.getSeatCount() === 0) {
      this.modal.close();
    }

    this.update();
  };

  getSeats() {
    return this.seats;
  }

  getSeatCount() {
    return this.seats.size;
  }

  maximumSeatCountReached() {
    const maxSeats = this.syosController.getMaximumSeatCount() || Infinity;

    return this.seats.size >= maxSeats;
  }

  empty() {
    this.seats.forEach((seat) => seat.removeFromCart());
  }

  update() {
    const shouldRender = this.getSeatCount() > 0;

    if (shouldRender) {
      const seats = Array.from(this.seats.values());
      const subtotal = this.getSubtotalDisplay();

      this.$modalBody.innerHTML = templateCart.render({ seats, subtotal });

      this.updateModalConfirmButtonState();
    }

    this.syosController.emit(SyosEvents.CART_UPDATE, { cart: this });
  }

  private updatePriceTypeSelectionHelper() {
    const selectedPriceTypeIds = [...this.seats.values()]
      .map((s) => s.selectedPriceTypeId);

    this.priceTypeSelectionHelper.updateSelections(selectedPriceTypeIds);
  }

  updateModalConfirmButtonState() {
    if (this.priceTypeSelectionHelper.mode === 'flex' && this.modal) {
      const $btnConfirm = this.modal.getButtonConfirm();

      if (this.priceTypeSelectionHelper.isSelectionComplete) {
        dom.enable($btnConfirm);
      } else {
        dom.disable($btnConfirm);
      }
    }
  }

  onModalBodyClick = (event) => {
    const $btn = dom.closest(event.target, '.tn-syos-cart-seat__btn-remove');

    if ($btn) {
      event.preventDefault();
      this.removeSeatForButton($btn as HTMLButtonElement);
    }
  };

  removeSeatForButton = async ($btn: HTMLButtonElement) => {
    const seatId = parseInt($btn.getAttribute('data-tn-seat-id')!, 10);

    const seat = this.seats.get(seatId);

    if (!seat) {
      return;
    }

    const isConfirmed = await SyosRemoveSeatConfirmationModal.confirm(
      this.syosController,
      seat,
      $btn,
    );

    if (isConfirmed) {
      seat.removeFromCart();

      if (this.seats.size > 0) {
        const $modalHeading = document.querySelector<HTMLElement>(`#${templateCart.HEADING_ID}`);
        $modalHeading?.setAttribute('tabindex', '-1');
        $modalHeading?.focus();
      }
    }
  };

  destroy() {
    this.$modalBody.removeEventListener('click', this.onModalBodyClick);

    this.seats.clear();
  }
}

export default SyosCartContainer;
